/**
 * Settings page with admin user management
 */
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Shield,
  Users,
  AlertCircle,
  Building2,
  Inbox,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '@/shared/hooks/useAuth';
import { haptic } from '@/shared/utils/telegram';
import { UserManagement } from '@/features/admin/components/UserManagement';
import { useUnmappedMisses } from '@/features/registry/hooks/useRegistry';

export function SettingsPage() {
  const navigate = useNavigate();
  const { user, isAdmin, logout } = useAuth();
  // Admin-only, and only fetched for admins -- non-admins never hit this endpoint.
  const unmappedQuery = useUnmappedMisses(isAdmin);
  const unmappedCount = (unmappedQuery.data ?? []).filter((m) => m.resolvedBrandId === null).length;

  const goTo = (path: string) => {
    haptic.impact('light');
    navigate(path);
  };

  const handleBack = () => {
    haptic.impact('light');
    navigate(-1);
  };

  const handleLogout = () => {
    haptic.notification('warning');
    logout();
    navigate('/brands');
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <AlertCircle size={48} className="text-[var(--tg-theme-hint-color)]" />
        <p className="text-sm text-[var(--tg-theme-hint-color)]">Not authenticated</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-20 animate-in fade-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center gap-3 sticky top-0 bg-[var(--tg-theme-bg-color)] z-20 pt-2 pb-4">
        <button
          onClick={handleBack}
          className="p-2 rounded-full hover:bg-[var(--tg-theme-secondary-bg-color)] transition-colors"
          aria-label="Back"
        >
          <ArrowLeft size={20} className="text-[var(--tg-theme-hint-color)]" />
        </button>
        <h1 className="text-2xl font-black uppercase tracking-tighter">Settings</h1>
      </div>

      {/* User Info Section */}
      <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-2xl p-5 space-y-4 border border-black/5 dark:border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[var(--tg-theme-link-color)]/10 flex items-center justify-center">
            <User size={24} className="text-[var(--tg-theme-link-color)]" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-[var(--tg-theme-text-color)]">
              {user.firstName} {user.lastName}
            </h3>
            {user.username && (
              <p className="text-xs text-[var(--tg-theme-hint-color)]">@{user.username}</p>
            )}
          </div>
        </div>

        <div className="space-y-2 pt-2 border-t border-black/5 dark:border-white/10">
          <div className="flex justify-between text-xs">
            <span className="text-[var(--tg-theme-hint-color)] font-medium">Telegram ID</span>
            <span className="font-mono font-bold text-[var(--tg-theme-text-color)]">
              {user.telegramUserId}
            </span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-[var(--tg-theme-hint-color)] font-medium">Role</span>
            <div className="flex items-center gap-1.5">
              {isAdmin && <Shield size={12} className="text-emerald-500" />}
              <span
                className={`font-bold uppercase text-xs px-2 py-0.5 rounded ${
                  isAdmin ? 'bg-emerald-500/10 text-emerald-600' : 'bg-blue-500/10 text-blue-600'
                }`}
              >
                {user.role}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Section: Brand Registry */}
      {isAdmin && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 px-1">
            <Building2 size={18} className="text-[var(--tg-theme-hint-color)]" />
            <h2 className="text-sm font-black text-[var(--tg-theme-hint-color)] uppercase tracking-wider">
              Brand Registry
            </h2>
          </div>

          <button
            onClick={() => goTo('/admin/programs')}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[var(--tg-theme-secondary-bg-color)] border border-black/5 dark:border-white/5 text-left active:scale-[0.98] transition-all"
          >
            <div className="w-10 h-10 rounded-xl bg-[var(--tg-theme-link-color)]/10 flex items-center justify-center flex-shrink-0">
              <Building2 size={18} className="text-[var(--tg-theme-link-color)]" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-[var(--tg-theme-text-color)]">
                Partner Programs
              </h3>
              <p className="text-xs text-[var(--tg-theme-hint-color)]">
                Platform, status, brand count
              </p>
            </div>
            <ChevronRight size={18} className="text-[var(--tg-theme-hint-color)]" />
          </button>

          <button
            onClick={() => goTo('/admin/unmapped')}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[var(--tg-theme-secondary-bg-color)] border border-black/5 dark:border-white/5 text-left active:scale-[0.98] transition-all"
          >
            <div className="w-10 h-10 rounded-xl bg-[var(--tg-theme-link-color)]/10 flex items-center justify-center flex-shrink-0">
              <Inbox size={18} className="text-[var(--tg-theme-link-color)]" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-[var(--tg-theme-text-color)]">
                Unmapped Inbox
              </h3>
              <p className="text-xs text-[var(--tg-theme-hint-color)]">Raw brand values to map</p>
            </div>
            {unmappedCount > 0 && (
              <span className="text-xs font-black px-2 py-1 rounded-full bg-rose-500/15 text-rose-600">
                {unmappedCount}
              </span>
            )}
            <ChevronRight size={18} className="text-[var(--tg-theme-hint-color)]" />
          </button>
        </div>
      )}

      {/* Admin Section */}
      {isAdmin && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 px-1">
            <Users size={18} className="text-[var(--tg-theme-hint-color)]" />
            <h2 className="text-sm font-black text-[var(--tg-theme-hint-color)] uppercase tracking-wider">
              User Access Management
            </h2>
          </div>

          {/* Info notice about role changes */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-bold text-blue-600 mb-1">Important Note</p>
                <p className="text-blue-600/80">
                  After changing a user's role or status, they will need to re-login (logout & login
                  again) for changes to take effect in their session.
                </p>
              </div>
            </div>
          </div>

          <UserManagement />
        </div>
      )}

      {/* Logout Button */}
      <button
        onClick={handleLogout}
        className="w-full py-3 px-4 bg-rose-500/10 text-rose-600 rounded-xl text-sm font-bold uppercase tracking-wide hover:bg-rose-500/20 transition-colors"
      >
        Logout
      </button>
    </div>
  );
}
